import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "../axios";
import { routeChips } from "../utils/routeColor";
import RegularBus from "../components/RegularBus";
import "../styles/analysis.css";

const DAY_TYPES = [
  { key: "weekday", label: "平日" },
  { key: "sat", label: "土曜" },
  { key: "holiday", label: "日祝" },
];

export default function AnalysisDutiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const depot = searchParams.get("depot") || "";
  const dayType = searchParams.get("day_type") || "weekday";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!depot) {
      setError("営業所が指定されていません。");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `/analysis/duties?depot=${encodeURIComponent(depot)}&day_type=${dayType}`
      );
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message ?? "取得に失敗しました");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [depot, dayType]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="anl-page">
      <button className="anl-back" onClick={() => navigate("/analysis")}>運用解析</button>

      <div className="anl-header">
        <h2 className="anl-title">
          乗番一覧
          {depot && <span className="anl-badge anl-badge--depot">{depot}</span>}
        </h2>
        <p className="anl-subtitle">
          目撃情報から推定した「1台が1日に担当する便の並び」です。実際の乗務番号とは対応しません。
        </p>
      </div>

      <div className="anl-tabs">
        {DAY_TYPES.map((t) => (
          <button
            key={t.key}
            className={`anl-tab ${dayType === t.key ? "anl-tab--active" : ""}`}
            onClick={() => setSearchParams({ depot, day_type: t.key })}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="anl-empty">読み込み中…</p>
      ) : error ? (
        <div className="anl-warning">{error}</div>
      ) : data.duties.length === 0 ? (
        <div className="anl-card">
          <p className="anl-empty">
            当該ダイヤ種別の乗番は推定できていません。<br />
            目撃情報の蓄積が不足している場合、乗番は表示されません。
          </p>
        </div>
      ) : (
        <div className="anl-card">
          <div className="anl-card__header">
            {data.depot_name}
            <span style={{ fontWeight: 400, color: "#6b7280" }}>{data.duties.length}乗番</span>
          </div>
          <div className="anl-table-wrap">
            <table className="anl-table">
              <thead>
                <tr>
                  <th>乗番</th>
                  <th>路線</th>
                  <th>所定</th>
                  <th className="anl-table__num">便数</th>
                  <th>始発</th>
                  <th>終発</th>
                  <th className="anl-table__num">接続率</th>
                </tr>
              </thead>
              <tbody>
                {data.duties.map((d) => {
                  // 行のどこを押しても乗番の詳細へ。この表は全列が同じ乗番を
                  // 説明しているので、行き先は1つに決まる。
                  const open = () => navigate(`/analysis/duties/${d.id}`);

                  return (
                  <tr key={d.id} className="anl-row-link" onClick={open}>
                    <td>
                      {/* 行クリックは指のための補助。キーボードと読み上げは
                          こちらのボタンを使うので、実ボタンのまま残す。 */}
                      <button
                        className="anl-link"
                        onClick={(e) => { e.stopPropagation(); open(); }}
                      >
                        {d.code}
                      </button>
                    </td>
                    <td>
                      {/* 乗番コードだけでは何線か思い浮かばない。
                          最大2つ・3路線以上をまとめる規則は routeChips が持つ。 */}
                      <span className="anl-chips">
                        {routeChips(d.routes).map((c) => (
                          <span
                            key={c.key}
                            className={`anl-chip${c.many ? " anl-chip--many" : ""}`}
                            style={c.style}
                          >
                            {c.label}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td>
                      {/* いつもこの乗番に入っている車。今日の話ではない */}
                      <RegularBus regular={d.regular} />
                    </td>
                    <td className="anl-table__num">{d.trip_count}</td>
                    <td className="anl-table__num">{d.first_departure}</td>
                    <td className="anl-table__num">{d.last_departure}</td>
                    <td className="anl-table__num">
                      {d.trip_count === 1 ? (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      ) : (
                        `${Math.round(d.confidence * 100)}%`
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="anl-card__body" style={{ borderTop: "1px solid var(--anl-line)" }}>
            <p className="anl-note">
              便数1の乗番は、前後の接続を特定できなかった便です。観測が蓄積されると接続されます。<br />
              「接続率」は、その並びが観測全体に占める割合の平均です。値が低いほど信頼性が低くなります。<br />
              「所定」は、その乗番にいつも入っている車両です。直近10営業日のうち6日以上を同じ車両が
              占めた場合に表示します。<strong>今日その車両が入っているという意味ではありません。</strong>
              観測が10営業日分たまっていない営業所・ダイヤ種別では、全て「—」になります。
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
